import {
  deleteTimeEntryService,
  getAllTimeEntriesService,
  getTimeEntryService,
  updateTimeEntryService,
} from "@/service/time-entry.service";
import { TimeEntryQuery } from "@/types/time-entry.model";
import { ROLES } from "@/utils/constants";
import { successToast } from "@/utils/toast";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import moment from "moment";
import { selectSelectedOrganization } from "./clientOrganizationSlice";

// Define TimeEntry interface for UI display
export interface TimeEntryDisplay {
  id: string;
  user_id?: string;
  user_name?: string;
  project_id?: string;
  project_name?: string;
  client_id?: string;
  client_name?: string;
  clock_in_date: string;
  clock_in_time: string;
  clock_out_date: string;
  clock_out_time: string;
  duration: string;
  service_item?: string;
  billable: boolean;
  notes?: string;
  type?: string;
  created_at?: string;
  updated_at?: string;
}

// Define TimeEntriesState interface
interface TimeEntriesState {
  entries: TimeEntryDisplay[];
  projectType: string;
  projectTypeFilter: string;
  filteredEntries: TimeEntryDisplay[];
  searchTerm: string;
  projectFilter: string;
  userFilter: string;
  billableFilter: string;
  dateRangeFilter: {
    startDate: string;
    endDate: string;
  };
  loading: boolean;
  error: string | null;
  noEntries: boolean;
  totalDuration: string;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Define initial state
const initialState: TimeEntriesState = {
  entries: [],
  projectType: "billable",
  projectTypeFilter: "",
  filteredEntries: [],
  searchTerm: "",
  projectFilter: "All projects",
  userFilter: "All users",
  billableFilter: "All entries",
  dateRangeFilter: {
    startDate: "",
    endDate: "",
  },
  loading: false,
  error: null,
  noEntries: false,
  totalDuration: "00:00",
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 20,
  },
};

// Create async thunks for time entries
// Thunk action to fetch time entries from API
export const fetchTimeEntries = createAsyncThunk(
  "timeEntries/fetchTimeEntries",
  async (
    query: TimeEntryQuery = { page: 1, pageSize: 20 },
    { dispatch, getState }
  ) => {
    try {
      dispatch(setLoading(true));

      // Get current filters from state if not provided in query
      const state = getState() as RootState;

      const loggedUser = state.auth.user;

      const isAdminOrClient =
        loggedUser?.role_code === ROLES.ADMIN ||
        loggedUser?.role_code === ROLES.CLIENT;

      const currentQuery: TimeEntryQuery = {
        page: query.page || state.timeEntries.pagination.currentPage,
        pageSize: query.pageSize || state.timeEntries.pagination.pageSize,
        search:
          query.search !== undefined
            ? query.search
            : state.timeEntries.searchTerm || undefined,
        projectId:
          query.projectId !== undefined
            ? query.projectId
            : state.timeEntries.projectFilter !== "All projects"
              ? state.timeEntries.projectFilter
              : undefined,
        userId:
          query.userId !== undefined
            ? query.userId
            : state.timeEntries.userFilter !== "All users"
              ? state.timeEntries.userFilter
              : undefined,
        isBillable:
          query.isBillable !== undefined
            ? query.isBillable
            : state.timeEntries.billableFilter !== "All entries"
              ? state.timeEntries.billableFilter === "Billable"
                ? 1
                : 0
              : undefined,
        startDate:
          query.startDate !== undefined
            ? query.startDate
            : state.timeEntries.dateRangeFilter.startDate
              ? moment(
                state.timeEntries.dateRangeFilter.startDate,
                "YYYY-MM-DD"
              ).format("DD-MM-YYYY")
              : undefined,
        endDate:
          query.endDate !== undefined
            ? query.endDate
            : state.timeEntries.dateRangeFilter.endDate
              ? moment(
                state.timeEntries.dateRangeFilter.endDate,
                "YYYY-MM-DD"
              ).format("DD-MM-YYYY")
              : undefined,
        projectType:
          query.projectType !== undefined
            ? query.projectType
            : state.timeEntries.projectType,
      };

      // If user is not admin, filter by user id
      if (!isAdminOrClient) {
        currentQuery.userId = loggedUser?.id;
      }

      // For client users, add orgId from selected organization
      if (loggedUser?.role_code === ROLES.CLIENT) {
        const selectedOrg = selectSelectedOrganization(state);
        if (selectedOrg?.client_id) {
          currentQuery.orgId = selectedOrg.client_id;
        }
      }

      // Make API call to get time entries
      const {
        data: { data, pagination, total_duration },
      } = await getAllTimeEntriesService(currentQuery);

      // Map API response to our TimeEntryDisplay model
      const timeEntries = data.map(
        (entry: any) =>
        ({
          id: entry.id,
          user_id: entry.user_id,
          user_name: entry.user ? entry.user.name : "Unknown User",
          project_id: entry.project_id,
          project_name: entry.project
            ? entry.project.name
            : "Unknown Project",
          client_id: entry.client ? entry.client.id : undefined,
          client_name: entry.client ? entry.client.name : "Unknown Client",
          clock_in_date: entry?.clock_in_date,
          clock_in_time: entry?.clock_in_time,
          clock_out_date: entry?.clock_out_date,
          clock_out_time: entry?.clock_out_time,
          duration: entry.duration || "00:00",
          service_item: entry.service,
          billable: entry.is_billable,
          notes: entry.notes,
          type: entry.entry === "MANUAL" ? "Manual" : "Real time",
          created_at: entry.created_at,
          updated_at: entry.updated_at,
        } as TimeEntryDisplay)
      );

      dispatch(setNoEntries(false));

      // Extract pagination info from response
      return {
        entries: timeEntries,
        totalDuration: total_duration || "00:00",
        pagination: {
          currentPage: pagination?.page as number,
          totalPages: pagination?.totalPages as number,
          totalItems: pagination?.total as number,
          pageSize: pagination?.pageSize as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoEntries(true));
        throw error;
      }

      const message =
        error.response?.data?.message || "Failed to fetch time entries";
      dispatch(setError(message));

      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Thunk action to get time entry details from API
export const getTimeEntryDetail = createAsyncThunk(
  "timeEntries/getTimeEntryDetail",
  async (entryId: string, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));

      // Make API call to get time entry details
      const {
        data: { data: entry },
      } = await getTimeEntryService(entryId);

      // Map API response to our TimeEntryDisplay model
      const timeEntry: TimeEntryDisplay = {
        id: entry.id,
        user_id: entry.user?.id,
        user_name: entry.user?.name,
        project_id: entry.project?.id,
        project_name: entry.project?.name,
        client_id: entry.client?.id,
        client_name: entry.client?.name,
        clock_in_date: moment(entry.clock_in_date, "DD-MM-YYYY").format(
          "YYYY-MM-DD"
        ),
        clock_in_time: entry.clock_in_time,
        clock_out_date: moment(entry.clock_out_date, "DD-MM-YYYY").format(
          "YYYY-MM-DD"
        ),
        clock_out_time: entry.clock_out_time,
        duration: entry.duration || "00:00",
        service_item: entry.service,
        billable: entry.is_billable,
        notes: entry.notes,
        type: entry.entry,
      };

      return timeEntry;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to fetch time entry details";
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Thunk action to update time entry in API
export const updateTimeEntry = createAsyncThunk(
  "timeEntries/updateTimeEntry",
  async (
    entryData: TimeEntryDisplay,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      dispatch(setLoading(true));

      // Map our TimeEntry model to API expected format
      const payload: any = {
        user_id: entryData.user_id,
        project_id: entryData.project_id,
        clock_in_date: moment(entryData.clock_in_date, "YYYY-MM-DD").format(
          "DD-MM-YYYY"
        ),
        clock_in_time: entryData.clock_in_time
          ? moment(entryData.clock_in_time, "hh:mm A").format("HH:mm")
          : "",
        clock_out_date: entryData.clock_out_date
          ? moment(entryData.clock_out_date, "YYYY-MM-DD").format("DD-MM-YYYY")
          : "",
        clock_out_time: entryData.clock_out_time
          ? moment(entryData.clock_out_time, "hh:mm A").format("HH:mm")
          : "",
        notes: entryData.notes,
        duration: entryData.duration,
        is_billable: entryData.billable,
      };

      if (
        !payload.clock_in_time ||
        !payload.clock_out_date ||
        !payload.clock_out_time
      ) {
        delete payload.clock_in_time;
        delete payload.clock_out_date;
        delete payload.clock_out_time;
      } else {
        delete payload.duration;
      }

      await updateTimeEntryService(entryData.id, payload);

      successToast("Success", "Time entry updated successfully");

      const state = getState() as RootState;
      dispatch(
        fetchTimeEntries({
          page: state.timeEntries.pagination.currentPage,
          pageSize: state.timeEntries.pagination.pageSize,
        })
      );

      return entryData;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to update time entry";
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Thunk action to delete time entry from API
export const deleteTimeEntry = createAsyncThunk(
  "timeEntries/deleteTimeEntry",
  async (entryId: string, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setLoading(true));

      await deleteTimeEntryService(entryId);

      successToast("Success", "Time entry deleted successfully");

      const state = getState() as RootState;
      dispatch(
        fetchTimeEntries({
          page: state.timeEntries.pagination.currentPage,
          pageSize: state.timeEntries.pagination.pageSize,
        })
      );

      return entryId;
    } catch (error: any) {
      const message =
        error.response?.data?.message || "Failed to delete time entry";
      dispatch(setError(message));
      return rejectWithValue(message);
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// Create the time entries slice
const timeEntriesSlice = createSlice({
  name: "timeEntries",
  initialState,
  reducers: {
    setNoEntries: (state, action: PayloadAction<boolean>) => {
      state.noEntries = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setProjectFilter: (state, action: PayloadAction<string>) => {
      state.projectFilter = action.payload;
    },
    setUserFilter: (state, action: PayloadAction<string>) => {
      state.userFilter = action.payload;
    },
    setBillableFilter: (state, action: PayloadAction<string>) => {
      state.billableFilter = action.payload;
    },
    setProjectType: (state, action: PayloadAction<string>) => {
      state.projectType = action.payload;
    },
    setProjectTypeFilter: (state, action: PayloadAction<string>) => {
      state.projectTypeFilter = action.payload;
    },
    setDateRangeFilter: (
      state,
      action: PayloadAction<{ startDate: string; endDate: string }>
    ) => {
      state.dateRangeFilter = action.payload;
    },
    resetFilters: (state) => {
      state.searchTerm = "";
      state.projectFilter = "All projects";
      state.userFilter = "All users";
      state.billableFilter = "All entries";
      state.projectTypeFilter = "";
      state.dateRangeFilter = { startDate: "", endDate: "" };
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Handle fetchTimeEntries
      .addCase(fetchTimeEntries.fulfilled, (state, action) => {
        state.entries = action.payload.entries;
        state.filteredEntries = action.payload.entries;
        state.totalDuration = action.payload.totalDuration;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchTimeEntries.rejected, (state, action) => {
        state.error = action.error.message || "Failed to fetch time entries";
        state.loading = false;
      })

      // Handle getTimeEntryDetail
      .addCase(getTimeEntryDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(getTimeEntryDetail.rejected, (state, action) => {
        state.error =
          action.error.message || "Failed to fetch time entry details";
        state.loading = false;
      })

      // Handle updateTimeEntry
      .addCase(updateTimeEntry.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(updateTimeEntry.rejected, (state, action) => {
        state.error = action.error.message || "Failed to update time entry";
        state.loading = false;
      })

      // Handle deleteTimeEntry
      .addCase(deleteTimeEntry.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteTimeEntry.rejected, (state, action) => {
        state.error = action.error.message || "Failed to delete time entry";
        state.loading = false;
      });
  },
});

export const {
  setNoEntries,
  setLoading,
  setError,
  setSearchTerm,
  setProjectFilter,
  setUserFilter,
  setBillableFilter,
  setProjectType,
  setProjectTypeFilter,
  setDateRangeFilter,
  resetFilters,
  resetAllState,
} = timeEntriesSlice.actions;

// Selectors
export const selectTimeEntries = (state: RootState) =>
  state.timeEntries.entries;
export const selectFilteredTimeEntries = (state: RootState) =>
  state.timeEntries.filteredEntries;
export const selectSearchTerm = (state: RootState) =>
  state.timeEntries.searchTerm;
export const selectProjectFilter = (state: RootState) =>
  state.timeEntries.projectFilter;
export const selectUserFilter = (state: RootState) =>
  state.timeEntries.userFilter;
export const selectBillableFilter = (state: RootState) =>
  state.timeEntries.billableFilter;
export const selectProjectType = (state: RootState) =>
  state.timeEntries.projectType;
export const selectProjectTypeFilter = (state: RootState) =>
  state.timeEntries.projectTypeFilter;
export const selectDateRangeFilter = (state: RootState) =>
  state.timeEntries.dateRangeFilter;
export const selectTimeEntriesLoading = (state: RootState) =>
  state.timeEntries.loading;
export const selectTimeEntriesError = (state: RootState) =>
  state.timeEntries.error;
export const selectTimeEntriesPagination = (state: RootState) =>
  state.timeEntries.pagination;
export const selectNoTimeEntries = (state: RootState) =>
  state.timeEntries.noEntries;
export const selectTotalDuration = (state: RootState) =>
  state.timeEntries.totalDuration;

export default timeEntriesSlice.reducer;
